/**
 * Keeps the session alive while a person is actually using the page.
 *
 * The access token lives fifteen minutes and was only ever renewed by a request to the server.
 * Filling in a long form — a purchase, a new product — calls the server rarely or never, so the
 * "your session will end" banner counted down under someone typing into it, and "click anywhere"
 * did nothing, because a click that sends no request renews nothing.
 *
 * Here a real input — a click, a key, a scroll, a touch — renews the token once it is inside the
 * last few minutes of its life. Somebody working never sees the banner; somebody who walked away
 * sees it after the idle time it describes, and their first click on return clears it.
 */

import { useEffect } from 'react';

import { getAccessTokenExpiresAt } from '../auth/tokenStore.js';
import { useKeepSessionAliveMutation } from './endpoints/auth.js';

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

/** One renew per burst of activity, not one per keystroke. */
const MIN_GAP_MS = 20_000;

/**
 * @param renewWithinMs Renew on activity once the token has this long left. Longer than the
 * banner's two-minute window, so activity just before it keeps it from ever appearing.
 */
export function useKeepSessionAlive(renewWithinMs = 180_000): void {
  const [keepAlive] = useKeepSessionAliveMutation();

  useEffect(() => {
    let lastAttempt = 0;

    const onActivity = () => {
      const expiresAt = getAccessTokenExpiresAt();
      if (expiresAt === 0) return;

      const now = Date.now();
      const remaining = expiresAt - now;
      // Already expired is the base query's case (PIN on the till, continue on the dashboard),
      // and far from expiry needs nothing.
      if (remaining <= 0 || remaining > renewWithinMs) return;
      if (now - lastAttempt < MIN_GAP_MS) return;

      lastAttempt = now;
      void keepAlive();
    };

    for (const name of ACTIVITY_EVENTS) window.addEventListener(name, onActivity, { capture: true, passive: true });
    return () => {
      for (const name of ACTIVITY_EVENTS) window.removeEventListener(name, onActivity, { capture: true });
    };
  }, [keepAlive, renewWithinMs]);
}
